<script setup>
import { computed, ref } from 'vue'
import UmisisModal from '../../../../../../../resources/js/components/umisis/generic/UmisisModal.vue'
import { useModalControl } from '../../../../../../../resources/js/composables/modal/useUmisisModal.js'
import { Field, FieldDescription, FieldLabel } from '../../../../../../../resources/js/components/ui/field/index.js'
import { useChecklistTemplatesStore } from '../../stores/checklistTemplatesStore.js'

const props = defineProps({
  checklistTemplate: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['imported'])
const { componentRef: dialog, openModal, closeModal } = useModalControl()
const checklistTemplatesStore = useChecklistTemplatesStore('import-items-modal')

const content = ref('')
const loading = ref(false)
const errorMessage = ref(null)

const checklistTemplateId = computed(() => Number(props.checklistTemplate?.id ?? 0))
const canSubmit = computed(() => {
  return !loading.value
    && checklistTemplateId.value > 0
    && props.checklistTemplate?.status === 'draft'
    && content.value.trim().length > 0
})

const fieldControlBaseClass = 'flex min-h-56 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50'

const onShow = () => {
  content.value = ''
  errorMessage.value = null
}

const handleOk = async (bvEvent) => {
  if (bvEvent) {
    bvEvent.preventDefault()
  }

  errorMessage.value = null

  if (!canSubmit.value) {
    errorMessage.value = 'Paste the checklist items you want to import.'
    return
  }

  loading.value = true

  try {
    const updatedChecklistTemplate = await checklistTemplatesStore.importChecklistTemplateItems(
      checklistTemplateId.value,
      content.value,
    )

    closeModal()
    emit('imported', updatedChecklistTemplate)
  } catch (error) {
    errorMessage.value = error.response?.data?.message ?? error.message ?? 'Unable to import checklist template items.'
  } finally {
    loading.value = false
  }
}

defineExpose({
  openModal,
  closeModal,
})
</script>

<template>
  <UmisisModal
    ref="dialog"
    title="Import Checklist Items"
    description="Paste a list or plain text. Each non-empty line will become a checklist item."
    ok-title="Import Items"
    cancel-title="Cancel"
    size="lg"
    :ok-disabled="!canSubmit"
    @ok="handleOk"
    @show="onShow"
  >
    <div class="flex flex-col gap-4">
      <Field>
        <FieldLabel for="checklist-template-import-content">
          Paste items
        </FieldLabel>
        <textarea id="checklist-template-import-content" v-model="content" rows="12" :class="fieldControlBaseClass" placeholder="1. First checklist item&#10;2. Second checklist item&#10;- Third checklist item" />
        <FieldDescription>
          Numbered and bulleted lists are supported. Parsing happens on the server before the checklist items are created.
        </FieldDescription>
      </Field>

      <FieldDescription v-if="errorMessage" class="font-medium text-destructive">
        {{ errorMessage }}
      </FieldDescription>
    </div>
  </UmisisModal>
</template>
